<template>
  <Teleport to="body">
    <div v-if="isOpen" class="image-flipbook-overlay">
      <!-- Close button -->
      <button
        class="image-flipbook-close-btn"
        @click="close"
        aria-label="Zavřít"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <!-- Main flipbook container -->
      <div class="image-flipbook-container">
        <Flipbook
          v-if="pagesArray.length > 0"
          ref="flipbookRef"
          class="image-flipbook"
          :pages="pagesArray"
          :zooms="[1, 1.5, 2]"
          :start-page="1"
          :ambient-light="0.8"
          :clickable="true"
        />

        <!-- Loading state -->
        <div v-else class="image-flipbook-loading">
          <div class="spinner"></div>
          <p>Načítání magazínu...</p>
        </div>
      </div>

      <!-- Controls outside flipbook, positioned on overlay -->
      <div v-if="pagesArray.length > 0" class="image-flipbook-controls">
        <div class="controls-left">
          <button
            @click="previousPage"
            :disabled="currentPage <= 1"
            aria-label="Předchozí strana"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <span class="page-info">
            Strana {{ currentPage }} / {{ totalPages }}
          </span>

          <button
            @click="nextPage"
            :disabled="currentPage >= totalPages"
            aria-label="Následující strana"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        <div class="controls-right">
          <a
            v-if="pdfUrl"
            :href="pdfUrl"
            download
            class="download-btn"
            aria-label="Stáhnout PDF"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </a>

          <button @click="zoomOut" aria-label="Oddálit">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="8" y1="11" x2="14" y2="11"></line>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </button>

          <button @click="zoomIn" aria-label="Přiblížit">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="11" y1="8" x2="11" y2="14"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Flipbook from '@/vendor/flipbook-vue/dist/flipbook.mjs'

interface Props {
  imageUrls: string[]
  pdfUrl?: string
}

const props = defineProps<Props>()

const isOpen = ref(false)
const flipbookRef = ref<any>(null)
const currentPage = ref(1)

// flipbook-vue requires null as first and last page for proper book-like behavior
// Create the array ONCE and never recompute it to avoid reactivity loops
const pagesArray: (string | null)[] = props.imageUrls.length === 0
  ? []
  : [null, ...props.imageUrls, null]

const totalPages = props.imageUrls.length

const open = () => {
  isOpen.value = true
  currentPage.value = 1
  // Disable body scroll
  document.body.style.overflow = 'hidden'
}

const close = () => {
  isOpen.value = false
  // Enable body scroll
  document.body.style.overflow = ''
}

const nextPage = () => {
  if (!flipbookRef.value || currentPage.value >= totalPages) return
  flipbookRef.value.flipRight()
  currentPage.value = Math.min(currentPage.value + 1, totalPages)
}

const previousPage = () => {
  if (!flipbookRef.value || currentPage.value <= 1) return
  flipbookRef.value.flipLeft()
  currentPage.value = Math.max(currentPage.value - 1, 1)
}

const zoomIn = () => {
  if (!flipbookRef.value) return
  flipbookRef.value.zoomIn()
}

const zoomOut = () => {
  if (!flipbookRef.value) return
  flipbookRef.value.zoomOut()
}

// Expose methods for parent component
defineExpose({
  open,
  close,
  nextPage,
  previousPage,
  zoomIn,
  zoomOut
})
</script>
